

import React, { useState, useEffect } from 'react';
import Parse from '../parseConfig';
import { useNavigate, useLocation } from 'react-router-dom';

import useUIPresentationMode from './helpers/useUIPresentationMode.js';

//import LogInBackgroundImage from '../images/LogInBackground.jpg';
import PlanHubLogo from '../images/PlanHubLogo.png';
import BackIcon from '../images/IconLeftArrowGrey@3x.png';
import EnvelopeIcon from '../images/EnvelopeIcon.png';
import PadlockIcon from '../images/PadlockIcon.png';


/*-----------------------------------------------------------------------------*/


const Login = ({ onLogin }) => {

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  const { presentationModeSize } = useUIPresentationMode();
  const hideLogo = presentationModeSize === 4 || presentationModeSize === 5;


  const handleLogin = async (e) => {

    e.preventDefault();

    setError('');  // Clear previous errors

    try {

      await Parse.User.logIn(username, password);

      // Try to retrieve the 'redirectTo' value from the current URL's query parameters
      // This is where RequireAuthRoute initially puts the intended destination, e.g., '?redirectTo=/main?p=abc123'
      const redirectTo = new URLSearchParams(location.search).get('redirectTo')

        // If it's not in the URL (e.g., user navigated back to the welcome screen), check sessionStorage
        // We store it there in RequireAuthRoute as a backup to persist through navigation changes
        || sessionStorage.getItem('redirectTo');

      // If a redirect path was found...
      if (redirectTo) {
        // Send the user to their originally intended URL
        navigate(redirectTo);

        // Remove the stored redirect value so it doesn't interfere with future navigation
        sessionStorage.removeItem('redirectTo');
      } else {
        // If there was no saved path, just send them to the default entry point (e.g. home/dashboard)
        navigate('/main');
      }

    }
    catch (err) {
      setError('Invalid email or password. Please try again.');
    }
  };


  // Scale this mofo depending on screen size.
  const [scaleFactor, setScaleFactor] = useState(0.9);

  useEffect(() => {
    if (presentationModeSize === 1 || presentationModeSize === 2) {  // Big!
      setScaleFactor(0.85);
    }
    if (presentationModeSize === 3) {  // Not as big.
      setScaleFactor(0.70);
    }
    if (presentationModeSize === 4 || presentationModeSize === 5) {  // Little!
      setScaleFactor(0.70);
    }
  }, [presentationModeSize]);


  /*-----------------------------------------------------------------------------*/


  return (

    <div className="welcome-screen" style={styles.container}>

      {/* Style Injection */}
      <style>{`
    input::placeholder {
      font-family: 'Roboto', sans-serif;
      font-size: 17px;
      color: rgba(255, 255, 255, 0.7);
      font-weight: 500;
      letter-spacing: 0.5px;
    }
  `}</style>

      <div
        style={{
          ...styles.loginBox,
          transform: `scale(${scaleFactor})`,
          transformOrigin: 'center',
          //animation: 'fadeIn 1.0s ease-in'
        }}
      >
        {!hideLogo && (
          <button
            type="button"
            onClick={() => navigate('/')}
            style={styles.backButtonTopLeft}
          >
            <img src={BackIcon} alt="Back" style={{ width: '32px', height: '32px' }} />
          </button>
        )}

        {!hideLogo && (
          <img
            src={PlanHubLogo}
            alt="PlanHub Logo"
            style={{ width: '180px', marginTop: '30px', marginBottom: '40px' }}
          />
        )}

        <h1 style={styles.heading}>Log In to PlanHub</h1>

        {error && <p style={styles.error}>{error}</p>}

        <form onSubmit={handleLogin} style={styles.form}>

          <div style={styles.inputWrapper}>
            <img src={EnvelopeIcon} alt="Email" style={styles.inputIcon} />
            <input
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder="Email"
              style={styles.input}
            />
          </div>

          <div style={styles.inputWrapper}>
            <img src={PadlockIcon} alt="Password" style={styles.inputIcon} />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Password"
              style={styles.input}
            />
          </div>

          <button type="submit" style={styles.button}>Log In</button>

        </form>

        {hideLogo && (
          <button
            type="button"
            onClick={() => navigate('/')}
            style={styles.backButtonBottom}
          >
            Back
          </button>
        )}

      </div>
    </div>

  );
};


/*-----------------------------------------------------------------------------*/


const styles = {
  container: {
    height: '100vh',
    display: 'flex',
    //backgroundImage: `url(${LogInBackgroundImage})`,
    //backgroundSize: 'cover',
    //backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#transparent',
    position: 'relative',
    overflow: 'hidden'
  },
  loginBox: {
    position: 'absolute',
    backdropFilter: 'blur(6px) grayscale(0.3)',
    backgroundColor: 'rgba(50, 50, 50, 0.25)',
    borderRadius: '23px',
    padding: '30px 30px',
    maxWidth: '550px',
    width: '90%',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)',
    textAlign: 'center',
  },
  heading: {
    fontSize: '35px',
    fontWeight: 800,
    color: 'var(--phblueaccent)',
    textShadow: '0 0px 10px rgba(255, 255, 255, 0.2)',
    marginBottom: '35px',
  },
  error: {
    color: 'red',
    fontSize: '14px',
    marginBottom: '10px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    marginTop: '20px',
  },
  inputWrapper: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    top: '22px',
    left: '12px',
    width: '30px',
    height: '30px',
    opacity: 1.0,
  },
  input: {
    padding: '12px 10px 12px 60px',  // Top Right Bottom Left. Padding left accommodates the icon.
    marginTop: '15px',
    fontSize: '18px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    color: '#fff',
    outline: 'none',
    width: '100%',
    minHeight: '45px',
    //boxSizing: 'border-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  button: {
    padding: '12px 12px',
    cursor: 'pointer',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: 'var(--phblueaccent)',
    fontSize: '17px',
    color: 'var(--phgrey)',
    fontWeight: '600',
    marginTop: '20px',
  },
  backButtonTopLeft: {
    position: 'absolute',
    top: '30px',
    left: '30px',
    background: 'none',
    opacity: 0.6,
    border: 'none',
    cursor: 'pointer',
    padding: '0',
  },
  backButtonBottom: {
    marginTop: '23px',
    fontSize: '15px',
    background: 'none',
    border: 'none',
    color: 'var(--phbluesuperwashedbackground)',
    textDecoration: 'none',
    cursor: 'pointer',
  }
};



export default Login;



